import React, { lazy } from 'react';
import { isTicketsAllowedEnvironment } from './util/environment';
import { isOcifOrganizationalUnit } from './util/organizationalUnit';

const ProcessLogin = lazy(() => import('./views/integration/ProcessLogin'));
const Login = lazy(() => import('./views/login/Login'));
const ForgotPassword = lazy(() => import('./views/ForgotPassword'));
const ResetPassword = lazy(() => import('./views/ResetPassword'));
const ThankyouMessage = lazy(() => import('./views/ThankyouMessage'));
const ConfirmationEmailForm = lazy(() => import('./views/ConfirmationEmailForm'));
const Home = lazy(() => import('./views/Home'));
const TrayView = lazy(() => import('./views/workflow/tray'));
const WorkflowNewEntity = lazy(() => import('./views/workflow/new'));
const WorkflowEntityDetails = lazy(() => import('./views/workflow/details'));
const WorkflowViewEntity = lazy(() => import('./views/workflow/view'));
const WorkflowEntityLog = lazy(() => import('./views/workflow/log'));
const AnonymousAccessView = lazy(() => import('./views/anonymous/AnonymousAccessView'));
const IntegrationUser = lazy(() => import('./views/users/IntegrationUser'));
const IntegrationUserTest = lazy(() => import('./views/users/test/IntegrationUserTest'));
const Profile = lazy(() => import('./views/Profile'));
const MyInstitution = lazy(() => import('./views/MyInstitution'));
const FirstLogin = lazy(() => import('./views/FirstLogin'));
const Faq = lazy(() => import('./views/Faq'));
const SystemSettings = lazy(() => import('./views/SystemSettings'));
const Help = lazy(() => import('./views/Help'));
const TicketPage = lazy(() => import('./views/TicketPage'));
const Tickets = lazy(() => import('./views/Tickets'));
const CreateItem = lazy(() => import('./views/SystemSettings/Invoce/CreateItem'));
const EditItem = lazy(() => import('./views/SystemSettings/Invoce/EditItem'));
const ListInvoiceItem = lazy(() => import('./views/SystemSettings/Invoce/ListInvoiceItem'));
const RedirectToPage = lazy(() => import('./views/redirect/RedirectToPage'));

const routes = {
  '/process-login': { component: ProcessLogin, excludeFromProfileCheck: true },
  '/login': { component: Login, default: 1, excludeFromProfileCheck: true },
  '/forgot-password': { component: ForgotPassword, excludeFromProfileCheck: true },
  '/reset-password/:token': { component: ResetPassword, excludeFromProfileCheck: true },
  '/register': { component: ResetPassword, excludeFromProfileCheck: true },

  '/thankyou': { exact: true, component: ThankyouMessage, excludeFromProfileCheck: true },
  '/confirmationEmailForm/:token': { exact: true, component: ConfirmationEmailForm, excludeFromProfileCheck: true },

  '/': { exact: true, component: Home, default: 10 },

  '/workflow/:slug/tray': { exact: true, component: TrayView, requires: 'login' },
  '/workflow/:entityType/new': { exact: true, component: WorkflowNewEntity, requires: 'login' },
  '/workflow/:entityType/:id': { exact: true, component: WorkflowEntityDetails, requires: 'login' },
  '/workflow/:entityType/:id/view': { exact: true, component: WorkflowViewEntity, requires: 'login' },
  '/workflow/:entityType/:id/log': { exact: true, component: WorkflowEntityLog, requires: 'permissions.ManageWorkflow' },

  '/anonymous/:tenant/': { component: AnonymousAccessView, excludeFromProfileCheck: true },

  '/integration/user/:jediid/settings': { component: IntegrationUser, exact: true, excludeFromProfileCheck: true },
  '/test/integration/users/iframe': { component: IntegrationUserTest, exact: true },

  '/profile': { component: Profile, exact: true, requires: 'login', excludeFromProfileCheck: true },
  '/my-institution': { component: MyInstitution, exact: true, requires: 'login' },
  '/firstLogin': { component: FirstLogin, exact: true, requires: 'login', excludeFromProfileCheck: true },

  '/faq': { component: Faq, exact: true, requires: 'login', excludeFromProfileCheck: true },

  '/system-settings': { component: SystemSettings, exact: true, requires: 'login' },
  // :id is optional so a tab (like Define Inventory Schedules) can link straight to one of its own
  // records — e.g. /system-settings/define-inventory-schedules/<fireId> — without a SEPARATE route entry.
  // A separate entry would give RouteSwitch's <Switch> a different `key` for the matched Route depending
  // on whether :id was present, which unmounts and remounts the whole tab (and everything it already
  // fetched) on every navigation between a tab's list and its own record — precisely the "loads twice"
  // behaviour this shape exists to avoid. The one cost: RouteSwitch's own pre-render regex for the
  // "redirect to /login?redirect=..." case is built straight from this path string, and an optional
  // segment makes that regex require BOTH segments — so an unauthenticated deep link to a bare tab
  // (no :id) falls back to a plain /login instead of preserving a return URL. Acceptable: the person still
  // ends up on /login either way, just without that one convenience.
  '/system-settings/:setting/:id?': { component: SystemSettings, exact: true, requires: 'login' },

  '/support': { component: Help, requires: 'login' },

  '/create-ticket': { component: TicketPage, requires: 'login' },
  // The Tickets module talks to a central UWE Ticket Hub that's only reachable from
  // local/staging/production — keep it out of the route table entirely elsewhere so a
  // direct link 404s instead of showing a page that can never load. Also restricted to
  // users in the "OCIF" organizational unit.
  ...(isTicketsAllowedEnvironment() ? { '/tickets': { component: Tickets, exact: true, requires: ['login', isOcifOrganizationalUnit] } } : {}),

  '/invoice/create': { component: CreateItem, exact: true, requires: 'login' },
  '/invoice/:id': { component: EditItem, exact: true, requires: 'login' },
  '/invoice': { component: ListInvoiceItem, exact: true, requires: 'login' },
  '/redirect/:endpoint': { component: RedirectToPage, exact: true, requires: 'login' },
};

export default routes;
